import type { ReactNode } from 'react';
import type {
  CategoryTileRow,
  CustomerProductCard,
  DiscoveryService,
  StoreService,
} from '@beautyburst/customer';
import {
  BRAND,
  Button,
  Card,
  CardGrid,
  CategoryTile,
  EmptyState,
  LinkButton,
  ProductCard,
  Section,
  StoreCard,
} from '@beautyburst/ui';
import type { StockState } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import type { CustomerApiClient } from '../api-client.ts';
import { CartAddFeedback, useCartAddFeedback } from '../cart-add-feedback.tsx';

/** The screen consumes the real discovery service result, without duplicating its schema. */
export type HomepageSections = Awaited<ReturnType<DiscoveryService['homepage']>>;
type HomepageSection = HomepageSections[number];
type NearbyStore = Awaited<ReturnType<StoreService['nearby']>>[number];
export type NearbyStores = readonly NearbyStore[];

export interface HomeProps {
  sections: HomepageSections;
  nearbyStores?: NearbyStores;
  nearbyStoresFailed?: boolean;
  onRetryNearbyStores?: () => void;
  leadCaptureSlot?: ReactNode;
  client?: Pick<CustomerApiClient, 'cart'>;
}

const categoryKind = 'CATEGORY_GRID';
const trendingKind = 'TRENDING_PRODUCTS';
const afterHoursKinds = new Set(['AFTER_HOURS', 'AFTER_HOURS_PRODUCTS']);
const nearbyStoreKinds = new Set(['NEARBY_STORES', 'NEARBY_BEAUTY_STORES']);
const recentKinds = new Set(['RECENTLY_VIEWED', 'RECENTLY_VIEWED_PRODUCTS']);

function sectionItems(sections: HomepageSections, kind: string): readonly unknown[] {
  return sections.filter((section) => section.kind === kind).flatMap((section) => section.items);
}

function sectionItemsForKinds(
  sections: HomepageSections,
  kinds: ReadonlySet<string>,
): readonly unknown[] {
  return sections.filter((section) => kinds.has(section.kind)).flatMap((section) => section.items);
}

function configuredSection(sections: HomepageSections, kind: string): HomepageSection | undefined {
  return sections.find((section) => section.kind === kind);
}

function isCategoryTile(value: unknown): value is CategoryTileRow {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Partial<CategoryTileRow>;
  return (
    typeof item.category_id === 'string' &&
    typeof item.slug === 'string' &&
    typeof item.name === 'string' &&
    (item.display_name === null || typeof item.display_name === 'string') &&
    (item.tile_media_key === null || typeof item.tile_media_key === 'string') &&
    (item.tile_alt_text === null || typeof item.tile_alt_text === 'string')
  );
}

function isCustomerProduct(value: unknown): value is CustomerProductCard {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Partial<CustomerProductCard>;
  return (
    typeof item.listingId === 'string' &&
    typeof item.productId === 'string' &&
    typeof item.productName === 'string' &&
    typeof item.productSlug === 'string' &&
    typeof item.variantName === 'string' &&
    typeof item.priceCents === 'number' &&
    (item.salePriceCents === null || typeof item.salePriceCents === 'number') &&
    typeof item.sellerDisplayName === 'string' &&
    (item.ownershipType === 'RETAILER' || item.ownershipType === 'BEAUTY_BURST_DIRECT') &&
    (item.available === null || typeof item.available === 'number') &&
    (item.mediaKey === null || typeof item.mediaKey === 'string') &&
    (item.mediaAltText === null || typeof item.mediaAltText === 'string')
  );
}

function isNearbyStore(value: unknown): value is NearbyStore {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Partial<NearbyStore>;
  return (
    typeof item.slug === 'string' &&
    typeof item.name === 'string' &&
    typeof item.sellerDisplayName === 'string' &&
    (item.ownershipType === 'RETAILER' || item.ownershipType === 'BEAUTY_BURST_DIRECT') &&
    (item.tagline === null || typeof item.tagline === 'string') &&
    typeof item.pickupEnabled === 'boolean' &&
    typeof item.deliveryEnabled === 'boolean' &&
    (item.distanceMiles === null || typeof item.distanceMiles === 'number') &&
    (item.averageRating === null || typeof item.averageRating === 'number') &&
    typeof item.reviewCount === 'number'
  );
}

function stockStateFor(product: CustomerProductCard): StockState | null {
  if (product.available === null) return null;
  return product.available > 0 ? 'IN_STOCK' : 'OUT_OF_STOCK';
}

function productImageAlt(product: CustomerProductCard): string {
  const explicitAlt = product.mediaAltText?.trim();
  return explicitAlt === undefined || explicitAlt.length === 0
    ? `${product.productName} ${product.variantName}`
    : explicitAlt;
}

function ProductGrid({
  label,
  products,
  emptyTitle,
  emptyDescription,
  client,
}: {
  label: string;
  products: readonly CustomerProductCard[];
  emptyTitle: string;
  emptyDescription: string;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const safeProducts = products.filter((product) => stockStateFor(product) !== null);
  if (safeProducts.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={<a href={customerHref('/categories')}>Shop Beauty Near You</a>}
      />
    );
  }

  return (
    <CardGrid label={label}>
      {safeProducts.map((product) => {
        const stockState = stockStateFor(product);
        if (stockState === null) return null;
        return (
          <HomepageProductCard
            key={product.listingId}
            product={product}
            stockState={stockState}
            client={client}
          />
        );
      })}
    </CardGrid>
  );
}

function HomepageProductCard({
  product,
  stockState,
  client,
}: {
  product: CustomerProductCard;
  stockState: StockState;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const { addToBag, dismiss, feedback } = useCartAddFeedback(client);
  return (
    <li>
      <ProductCard
        href={customerHref(`/products/${product.productSlug}`)}
        imageUrl={null}
        imageAlt={productImageAlt(product)}
        /*
         * DiscoveryService.homepage does not currently return a brand.
         * An empty value is intentionally preferable to inventing one.
         */
        brand=""
        name={product.productName}
        variantLabel={product.variantName}
        priceCents={product.priceCents}
        saleActive={product.saleActive}
        salePriceCents={product.salePriceCents}
        retailerName={
          product.ownershipType === 'BEAUTY_BURST_DIRECT' ? null : product.sellerDisplayName
        }
        stockState={stockState}
        canAddToBag={stockState !== 'OUT_OF_STOCK'}
        onAddToBag={() => void addToBag({ listingId: product.listingId, quantity: 1 })}
      />
      <CartAddFeedback feedback={feedback} onDismiss={dismiss} />
    </li>
  );
}

function Categories({ sections }: { sections: HomepageSections }) {
  const configured = configuredSection(sections, categoryKind);
  const categories = sectionItems(sections, categoryKind).filter(isCategoryTile);
  return (
    <Section
      title={configured?.title ?? 'Shop by Category'}
      subtitle={configured?.subtitle ?? null}
      ctaLabel={configured?.ctaLabel ?? null}
      ctaHref={configured?.ctaHref ?? null}
    >
      {categories.length === 0 ? (
        <EmptyState
          title="No categories available"
          description="Categories will appear here when they are available in your market."
          action={<a href={customerHref('/stores')}>Explore nearby stores</a>}
        />
      ) : (
        <CardGrid label="Shop by category" minColumnWidth="168px">
          {categories.map((category) => (
            <li key={category.category_id}>
              <CategoryTile
                href={customerHref(`/categories/${category.slug}`)}
                displayName={category.display_name ?? category.name}
                imageUrl={null}
                imageAlt={category.tile_alt_text ?? undefined}
              />
            </li>
          ))}
        </CardGrid>
      )}
    </Section>
  );
}

function NearbyStores({
  sections,
  nearbyStores,
  nearbyStoresFailed,
  onRetryNearbyStores,
}: {
  sections: HomepageSections;
  nearbyStores: NearbyStores | undefined;
  nearbyStoresFailed: boolean;
  onRetryNearbyStores?: () => void;
}) {
  if (nearbyStoresFailed) {
    return (
      <Section title="Nearby Beauty Stores">
        <EmptyState
          title="Nearby stores are unavailable"
          description="We could not load location-aware store information right now. Please try again."
          action={<a href={customerHref('/stores')}>View stores</a>}
        />
        {onRetryNearbyStores === undefined ? null : (
          <Button type="button" onClick={onRetryNearbyStores}>
            Retry nearby stores
          </Button>
        )}
      </Section>
    );
  }
  const stores =
    nearbyStores?.filter(isNearbyStore) ??
    sectionItemsForKinds(sections, nearbyStoreKinds).filter(isNearbyStore);
  return (
    <Section title="Nearby Beauty Stores">
      {stores.length === 0 ? (
        <EmptyState
          title="No nearby stores to show"
          description="Nearby stores will appear here when location-aware store data is available."
          action={<a href={customerHref('/categories')}>Browse beauty categories</a>}
        />
      ) : (
        <CardGrid label="Nearby beauty stores">
          {stores.map((store) => (
            <li key={store.locationId}>
              <StoreCard
                href={customerHref(`/stores/${store.slug}`)}
                storeName={store.name}
                tagline={store.tagline}
                averageRating={store.averageRating}
                reviewCount={store.reviewCount}
                distanceMiles={store.distanceMiles}
                pickupEnabled={store.pickupEnabled}
                deliveryEnabled={store.deliveryEnabled}
              />
            </li>
          ))}
        </CardGrid>
      )}
    </Section>
  );
}

function AfterHours({
  sections,
  client,
}: {
  sections: HomepageSections;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const products = sectionItemsForKinds(sections, afterHoursKinds).filter(isCustomerProduct);
  if (products.length === 0) return null;
  return (
    <Section title="Beauty After Hours">
      <ProductGrid
        label="Beauty products available after hours"
        products={products}
        emptyTitle="No after-hours products to show"
        emptyDescription="No eligible after-hours products are available right now."
        client={client}
      />
    </Section>
  );
}

function RecentlyViewed({
  sections,
  client,
}: {
  sections: HomepageSections;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const products = sectionItemsForKinds(sections, recentKinds).filter(isCustomerProduct);
  if (products.length === 0) return null;
  return (
    <Section title="Recently viewed">
      <ProductGrid
        label="Recently viewed products"
        products={products}
        emptyTitle="Nothing recently viewed"
        emptyDescription="Products you view will appear here."
        client={client}
      />
    </Section>
  );
}

function TrendingAndDirect({
  sections,
  client,
}: {
  sections: HomepageSections;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const configured = configuredSection(sections, trendingKind);
  const allProducts = sectionItems(sections, trendingKind).filter(isCustomerProduct);
  const trending = allProducts.filter((product) => product.ownershipType === 'RETAILER');
  const direct = allProducts.filter((product) => product.ownershipType === 'BEAUTY_BURST_DIRECT');
  return (
    <>
      <Section
        title={configured?.title ?? 'Trending Products'}
        subtitle={configured?.subtitle ?? null}
        ctaLabel={configured?.ctaLabel ?? null}
        ctaHref={configured?.ctaHref ?? null}
      >
        <ProductGrid
          label="Trending products"
          products={trending}
          emptyTitle="No trending products to show"
          emptyDescription="Trending products will appear here when available."
          client={client}
        />
      </Section>
      <Section title="Beauty Burst Direct">
        <ProductGrid
          label="Beauty Burst Direct products"
          products={direct}
          emptyTitle="No Beauty Burst Direct products to show"
          emptyDescription="Beauty Burst Direct products will appear here when available."
          client={client}
        />
      </Section>
    </>
  );
}

/**
 * The homepage is a pure renderer. It only displays availability-like wording
 * when it arrives in its input; this service result currently provides no such
 * field, so this screen never derives "open", "late", or overnight labels.
 */
export function Home({
  sections,
  nearbyStores,
  nearbyStoresFailed = false,
  onRetryNearbyStores,
  leadCaptureSlot,
  client,
}: HomeProps) {
  return (
    <>
      <Section title="Beauty shopping">
        <div style={{ display: 'grid', gap: space[3], padding: `${space[4]} 0` }}>
          <h1 style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}>
            {BRAND.tagline}
          </h1>
          <p style={{ ...typeStyle('bodyLg'), color: semanticColor.textSecondary, margin: 0 }}>
            {BRAND.customerPromise}
          </p>
          <LinkButton href={customerHref('/categories')}>Shop Beauty Near You</LinkButton>
        </div>
      </Section>

      <AfterHours sections={sections} client={client} />
      <Categories sections={sections} />
      <NearbyStores
        sections={sections}
        nearbyStores={nearbyStores}
        nearbyStoresFailed={nearbyStoresFailed}
        onRetryNearbyStores={onRetryNearbyStores}
      />
      <TrendingAndDirect sections={sections} client={client} />

      <Section title="Beauty Burst+">
        <Card>
          <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary, margin: 0 }}>
            Explore Beauty Burst+ benefits and program details.
          </p>
          <LinkButton
            href={customerHref('/beauty-burst-plus')}
            variant="ghost"
            style={{ marginTop: space[3] }}
          >
            Learn about Beauty Burst+
          </LinkButton>
        </Card>
      </Section>

      <RecentlyViewed sections={sections} client={client} />

      <Section title="Partner with Beauty Burst">
        <Card>
          <p style={{ ...typeStyle('body'), color: semanticColor.textPrimary, margin: 0 }}>
            Own a Beauty Supply Store? Partner With Beauty Burst.
          </p>
          <LinkButton
            href={customerHref('/partner')}
            variant="ghost"
            style={{ marginTop: space[3] }}
          >
            Learn about partnership
          </LinkButton>
        </Card>
      </Section>

      {leadCaptureSlot === null || leadCaptureSlot === undefined ? null : (
        <Section title="Stay connected">{leadCaptureSlot}</Section>
      )}
    </>
  );
}
